import React, { useState } from 'react';
import uploadImageIcon from '../../../../../../assests/img/upload-image-icon.svg';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { RemoveIcon } from '../../../../../../assests/GmsIcons';

interface UploadImageProps {
  id?: string;
  title?: string;
  launchIcon?: string;
  setFileParent: React.Dispatch<React.SetStateAction<File | null>>;
  onRemovePressed?: () => void;
}

const UploadImage = ({
  id,
  launchIcon,
  setFileParent,
  onRemovePressed,
}: UploadImageProps) => {
  const baseId = id || 'upload-image';
  const [file, setFile] = useState<File | null>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    const maxSize = 2 * 1024 * 1024;
    if (selectedFile) {
      if (selectedFile.size > maxSize) {
        toast.error('File size must be less than 2MB');
        return;
      }
      const validFormats = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!validFormats.includes(selectedFile.type)) {
        toast.error('Only image files (JPEG, PNG, GIF, WEBP) are allowed');
        setFile(null);
        setFileParent(null);
      } else {
        setFile(selectedFile);
        setFileParent(selectedFile);
      }
    }
  };

  return (
    <div
      id={baseId}
      data-testid={baseId}
      className="tw-w-full tw-px-[16px] tw-py-[14px] tw-bg-surface tw-rounded tw-border tw-border-edge tw-justify-between tw-items-center tw-inline-flex"
    >
      <>
        <input data-testid="popup-uploadimage-input-1"
          id={`${baseId}-input`}
          type="file"
          accept="image/jpeg, image/png, image/gif, image/webp"
          className="tw-hidden"
          onChange={handleFileChange}
        />
        {/* TODO(design-system): stays a <label> for the hidden file input — the
            DS store has no FileUpload and a DS Button cannot own an input. It
            wears the DS secondary button chrome (§ 102) so the dialog carries
            no popup-button-* classes. */}
        <label
          id={`${baseId}-input-label`}
          data-testid={`${baseId}-input-label`}
          htmlFor={`${baseId}-input`}
          className="tw-inline-flex tw-items-center tw-justify-center tw-h-9 tw-px-4 tw-rounded-ctl tw-text-title tw-cursor-pointer tw-bg-surface tw-text-accent tw-border tw-border-edge hover:tw-bg-accent-tint hover:tw-border-accent-hover"
        >
          Upload Image
        </label>
      </>

      <div id={`${baseId}-preview`} className="tw-w-[164px] tw-justify-items-center">
        <img
          id={`${baseId}-preview-image`}
          className="tw-object-cover tw-w-[164px]"
          src={
            file != null
              ? URL.createObjectURL(file)
              : (launchIcon ?? '').length > 0
                ? launchIcon
                : uploadImageIcon
          }
          alt={'uploadImageIcon'}
        />
        {file != null || (launchIcon ?? '').length > 0 ? (
          <div role="button"
            id={`${baseId}-remove-btn`}
            data-testid={`${baseId}-remove-btn`}
            onClick={() => {
              setFile(null);
              setFileParent(null);
              if (onRemovePressed) {
                onRemovePressed();
              }
            }}
            className="tw-text-tone-danger-ink [&_path]:tw-stroke-current [&_path]:tw-fill-surface tw-text-sm tw-font-normal tw-leading-tight tw-cursor-pointer tw-flex tw-items-center tw-pt-2"
          >
            <RemoveIcon />
            Remove Image
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default UploadImage;
